import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllAuctions } from "../redux/actions/auction";
import { Link } from "react-router-dom";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import {
  HiOutlineLightningBolt,
  HiOutlineClock,
} from "react-icons/hi";

const AuctionListPage = () => {
  const dispatch = useDispatch();
  const { auctions, isLoading } = useSelector((state) => state.auctions);
  const [statusFilter, setStatusFilter] = useState("live");

  useEffect(() => {
    dispatch(getAllAuctions(statusFilter));
  }, [dispatch, statusFilter]);

  const timeRemaining = (endTime) => {
    const diff = new Date(endTime) - new Date();
    if (diff <= 0) return "Ended";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    return `${hours}h ${mins}m`;
  };

  const statusBadge = (status) => {
    const colors = {
      scheduled: "bg-blue-100 text-blue-800",
      live: "bg-green-100 text-green-800",
      ended: "bg-gray-100 text-gray-800",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100"}`}>
        {status}
      </span>
    );
  };

  return (
    <div>
      <Header activeHeading={0} />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <HiOutlineLightningBolt className="w-8 h-8 text-[#086872]" />
              Live Auctions
            </h1>
            <p className="text-gray-500 mt-1">Bid on exclusive products at great prices</p>
          </div>
          <div className="flex gap-2">
            {["live", "scheduled", "ended", ""].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                  statusFilter === s
                    ? "bg-[#086872] text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {s || "All"}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#086872]" />
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {auctions && auctions.length > 0 ? auctions.map((auction) => (
              <Link
                key={auction._id}
                to={`/auction/${auction._id}`}
                className="group bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="relative">
                  {auction.product?.images?.[0]?.url ? (
                    <img
                      src={auction.product.images[0].url}
                      alt={auction.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                      <HiOutlineLightningBolt className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    {statusBadge(auction.status)}
                  </div>
                  {auction.status === "live" && (
                    <div className="absolute top-3 right-3 bg-red-600 text-white px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1 animate-pulse">
                      <HiOutlineClock className="w-3.5 h-3.5" />
                      {timeRemaining(auction.endTime)}
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-[#086872] transition-colors">
                    {auction.title}
                  </h3>
                  <p className="text-xs text-gray-500 truncate mb-3">{auction.product?.name}</p>

                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-xs text-gray-500">Current Bid</p>
                      <p className="text-xl font-bold text-[#086872]">
                        {auction.highestBid || auction.startingPrice}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{auction.totalBids || 0} bids</p>
                      {auction.status === "live" && (
                        <span className="text-xs font-medium text-green-600">Active</span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            )) : (
              <div className="col-span-full text-center py-16 text-gray-500">
                <HiOutlineLightningBolt className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-lg font-medium">No {statusFilter || ""} auctions right now</p>
                <p className="text-sm text-gray-400 mt-1">Check back soon for new auctions</p>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AuctionListPage;
